import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Colors } from "../../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import {
  accountActions,
  getAccountSlice,
  updateDataFilter,
} from "../../../redux/slices/accountSlice";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import TextHoverButton from "../../Buttons/TextHoverButton";
import { VerticalDivider } from "../../Dividers/VerticalDivider";
import {
  filterConversationsByEvent,
  liveFilterConversationsByEvent,
} from "../../../utils/conversationUtils";
import DropdownWithSearch from "../../DropdownsAndTabs/DropdownWithSearch";
import { getEventSlice } from "../../../redux/slices/eventSlice";
import {
  Conversation,
  DataFilter,
  DataFilterField,
  DataFilterType,
  Event,
  EventType,
} from "@markit/common.types";
import { filterUndefinedValues, foundUserProducts } from "@markit/common.utils";
import LabelButton from "../../Buttons/LabelButton";
import { fetchSingleUser } from "../../../utils/FetchSingleData";
import EventItemImage from "../../DisplayItem/EventItem/EventItemImage";
import ListTabs from "../../DropdownsAndTabs/ListTabs";
import { generate } from "shortid";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { getEventDisplayDate } from "../../../utils/GetLongDate";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";

type ConversationFilterModalProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  setFilteredConversations: Dispatch<SetStateAction<Conversation[]>>;
  loadingFilter: boolean;
  setLoadingFilter: (loadingFilter: boolean) => void;
};

const ConversationFilterModal = (props: ConversationFilterModalProps) => {
  const {
    showModal,
    setShowModal,
    setFilteredConversations,
    loadingFilter,
    setLoadingFilter,
  } = props;
  const { accountData, followingAccountData, dataFilters } =
    useSelector(getAccountSlice);
  const { events } = useSelector(getEventSlice);
  const dispatch = useDispatch();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [stateValue, setStateValue] = useState(0);

  const foundConversationFilter = useMemo(
    () =>
      dataFilters.find(
        (filter) => filter.type === DataFilterType.CONVERSATIONS
      ),
    [dataFilters]
  );

  const eventRelatedItems = useMemo(() => {
    const hostOnlyEvents = events.filter(
      (event) => event.createdBy === accountData.uid
    );
    const markitEvents = foundUserProducts(
      hostOnlyEvents,
      accountData.uid,
      [
        EventType.MARKIT,
        EventType.EXTERNAL_EVENTBRITE,
        EventType.EXTERNAL_SHOWCLIX,
      ],
      stateValue === 0 ? "active" : "inactive"
    );
    return stateValue === 0 ? markitEvents : markitEvents.reverse();
  }, [events, accountData.uid, stateValue]);

  const filteredEventItem = useMemo(() => {
    const foundEventRule = foundConversationFilter?.rules.find(
      (condition) => condition.field === DataFilterField.EVENT
    );
    if (foundEventRule) {
      const foundEvent = events.find(
        (event) => event.id === foundEventRule.values[0]
      );
      return foundEvent;
    }
  }, [foundConversationFilter, events]);

  const filterAndFetchConversations = useCallback(
    async (id: string) => {
      const filteredConversations = await filterConversationsByEvent(
        accountData.uid,
        id
      );
      setFilteredConversations(filteredConversations);

      // Add the users to the followingAccountData state from the found conversations
      const foundUsers = filterUndefinedValues(
        await Promise.all(
          filteredConversations.map((conversation) =>
            fetchSingleUser(conversation.uid, followingAccountData)
          )
        )
      );
      await dispatch(
        accountActions.addMultipleFollowingAccountData(foundUsers)
      );
    },
    [accountData.uid, followingAccountData, dispatch, setFilteredConversations]
  );

  const filteredEventId = useMemo(() => {
    return foundConversationFilter?.rules.find(
      (r) => r.field === DataFilterField.EVENT
    )?.values[0];
  }, [foundConversationFilter]);

  // This gets called when you are on the Inbox tab (within this file for centralization)
  // If there is a stored conversation filter, apply it immediately
  useAsyncOnMount(async () => {
    if (filteredEventId) {
      setLoadingFilter(true);
      await filterAndFetchConversations(filteredEventId);
      setLoadingFilter(false);
    }
  });

  useEffect(() => {
    // When a filter condition is applied, live update for new conversations that fall within the filter
    if (filteredEventId) {
      const unsubscribe = liveFilterConversationsByEvent(
        accountData.uid,
        filteredEventId,
        setFilteredConversations,
        followingAccountData,
        dispatch
      );
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEventId, accountData.uid]);

  const filterEventOnPress = useCallback(
    async (id: string) => {
      setLoadingFilter(true);
      await filterAndFetchConversations(id);

      // Updating existing filter
      if (foundConversationFilter) {
        await dispatch(
          updateDataFilter(
            accountData.uid,
            {
              ...foundConversationFilter,
              rules: foundConversationFilter.rules.map((rule) =>
                rule.field === DataFilterField.EVENT
                  ? { ...rule, values: [id] }
                  : rule
              ),
            },
            "update"
          )
        );
      } else {
        // Creating new filter
        const newDataFilter: DataFilter = {
          id: generate(),
          createdAt: new Date().toISOString(),
          logic: "and",
          type: DataFilterType.CONVERSATIONS,
          rules: [
            { field: DataFilterField.EVENT, operator: "is", values: [id] },
          ],
        };
        await dispatch(updateDataFilter(accountData.uid, newDataFilter, "add"));
      }

      setLoadingFilter(false);
    },
    [
      setLoadingFilter,
      filterAndFetchConversations,
      foundConversationFilter,
      dispatch,
      accountData.uid,
    ]
  );

  const resetFilterOnPress = useCallback(async () => {
    if (foundConversationFilter) {
      setFilteredConversations([]);
      setLoadingFilter(true);
      await dispatch(
        updateDataFilter(accountData.uid, foundConversationFilter, "remove")
      );
      setLoadingFilter(false);
    }
  }, [
    foundConversationFilter,
    setFilteredConversations,
    setLoadingFilter,
    dispatch,
    accountData.uid,
  ]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, info?: { source: "trigger" | "menu" }) => {
      if (info?.source === "trigger" || nextOpen) {
        setIsOpenDropdown(nextOpen);
      }
    },
    [setIsOpenDropdown]
  );

  return (
    <PopupModalContainer
      altPadding={0}
      positionOnTop={150}
      valueComp={
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
            <span className="sectionTitle">Filter Conversations</span>
            <span className="bodySubtext">
              Narrow down the visible conversations to show by adding a filter
              below.
            </span>
          </div>
          <HorizontalDivider altMargin={0} />
          <div className="ColumnNormal" style={{ gap: 14, paddingInline: 14 }}>
            <div className="AlignedRowSpaced">
              <span className="bodyMedium">
                In this view, show conversations
              </span>
              {foundConversationFilter ? (
                <TextHoverButton
                  text="Reset"
                  onPress={resetFilterOnPress}
                  textStyles={{
                    color: Colors.BLUE5,
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                />
              ) : null}
            </div>
            <div
              className="AlignedRow"
              style={{ gap: 14, paddingInline: 24, flex: 1 }}
            >
              <span className="bodyRegular">Where</span>
              <div
                className="AlignedRow"
                style={{
                  flex: 1,
                  border: `0.5px solid ${Colors.GRAY2}`,
                  height: 41,
                  borderRadius: 12,
                }}
              >
                <div className="AlignedColumn" style={{ minWidth: 120 }}>
                  <span className="bodyRegular">Event</span>
                </div>
                <VerticalDivider height={41} thickness={0.5} />
                <div className="AlignedColumn" style={{ minWidth: 70 }}>
                  <span className="bodyRegular">is</span>
                </div>
                <VerticalDivider height={41} thickness={0.5} />
                <div
                  style={{
                    flex: 1,
                    paddingInline: 14,
                    pointerEvents: loadingFilter ? "none" : "auto",
                  }}
                >
                  <DropdownWithSearch
                    items={eventRelatedItems}
                    trigger={["click"]}
                    isOpen={isOpenDropdown}
                    onOpenChange={handleOpenChange}
                    onItemPress={(item) => filterEventOnPress(item.id)}
                    getSearchableText={(item) => item.title}
                    renderItem={(item: Event) => (
                      <>
                        <EventItemImage item={item} size={36} />
                        <div className="ColumnNormal" style={{ gap: 4 }}>
                          <span className="bodyMedium OneLineText">
                            {item.title}
                          </span>
                          <span className="smallBodySubtext">
                            {getEventDisplayDate(item)}
                          </span>
                        </div>
                      </>
                    )}
                    renderSubHeader={
                      <ListTabs
                        tabLabels={["Active", "Inactive"]}
                        tabNumbers={[-1, -1]}
                        selectedValue={stateValue}
                        onChange={setStateValue}
                        altColor={Colors.BLACK}
                      />
                    }
                  >
                    <div
                      className="AlignedRowSpaced"
                      style={{ gap: 14 }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.opacity = "0.7")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.opacity = "1")
                      }
                    >
                      {filteredEventItem ? (
                        <LabelButton
                          text={filteredEventItem.title}
                          color={Colors.WHITE}
                          backgroundColor={Colors.BLUE5}
                          containerStyles={{
                            paddingInline: 10,
                            paddingBlock: 7,
                          }}
                        />
                      ) : (
                        <span className="bodySubtext">Select an option</span>
                      )}
                      <DropdownArrowIcon
                        isOpen={isOpenDropdown}
                        color={Colors.GRAY1}
                        height={18}
                      />
                    </div>
                  </DropdownWithSearch>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      noExit
      noDivider
      closeOnOutsidePress={!isOpenDropdown}
      closeModal={() => setShowModal(false)}
      hideModal={!showModal}
      modalContentWidth={PopupModalContainerWidth.WIDE}
    />
  );
};

export default memo(ConversationFilterModal);
